import { CheckCircle } from '@mui/icons-material';
import { AlertColor, Button } from '@mui/material';
import Form, { FormDataModel } from 'components/Form';
import PageLoader from 'components/PageLoader';
import { RefObject, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import NewBookingSendEmailComponent from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent';
import {
  appendEmailAttachmentOptions,
  getEmailAttachmentVisibility,
} from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent/emailAttachmentOptions';
import { NewBookingSendEmailFormComponent } from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent/SendEmailFormComponent';
import { User } from 'utils/redux/reducer/authentication-slice';
import { removeErrorFieldsFromValues } from 'utils/validators';
import { fetchErrorFields } from 'utils/validators/HelperFunctions';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import Modal from '../../../../components/modal/Modal';
import axiosInstance from '../../../../utils/axios';
import { URLS } from '../../../../utils/constants/urls';
import { bookingDetailToolbarButtonSx } from '../bookingDetailToolbarSx';

type ConfirmBookingButtonProps = {
  id: any;
  onSuccess: () => void;
  bookingEmail: any;
  /** Toolbar row: consistent sizing with other booking actions */
  compact?: boolean;
  isContractTemplatePresent?: boolean;
  isProposalTemplatePresent?: boolean;
  isPiTemplatePresent?: boolean;
  isEnquiryTemplatePresent?: boolean;
};
export const ConfirmBookingButton: React.FC<ConfirmBookingButtonProps> = ({
  bookingEmail,
  id,
  onSuccess,
  compact = false,
  isContractTemplatePresent = false,
  isProposalTemplatePresent = false,
  isPiTemplatePresent = false,
  isEnquiryTemplatePresent = false,
}) => {
  const emailAttachmentVisibility = getEmailAttachmentVisibility({
    isContractTemplatePresent,
    isProposalTemplatePresent,
    isPiTemplatePresent,
    isEnquiryTemplatePresent,
  });
  const loggedInUserDetails = useSelector(User);
  let sendEmailFormRef: RefObject<Form | null | undefined> = useRef();
  const [openSendEmailDrawer, setOpenSendEmailDrawer] = useState<boolean>(false);
  const [sendEmailHasError, setSendEmailHasError] = useState<boolean>(false);
  const [attachment, setAttachment] = useState<any>([]);
  const [deletedImage] = useState<any>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [sendEmailPageResponse, setSendEmailPageResponse] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  /** Sync guard so a second Save click in the same tick cannot start another PUT/POST. */
  const savingRef = useRef(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const handleClose = () => {
    setOpenModal(false);
  };
  const getFileDetails = (value: string, field: string) => {
    setAttachment(value);
  };

  const handleSaveWithSendEmail = async (e: any) => {
    e.preventDefault();
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    try {
      const { getFormData } = sendEmailFormRef.current as {
        getFormData: () => { formData: FormDataModel; isFormValid: boolean };
      };

      const { formData, isFormValid } = getFormData();
      setSendEmailHasError(false);

      if (isFormValid) {
        const emailFormData = removeErrorFieldsFromValues(formData);
        setIsLoading(true);
        handleClose();

        const statusRequest = {
          status: 'CONFIRMED',
        };

        const { status } = await axiosInstance.put(URLS.bookingStatus(id), statusRequest);
        if (status === 200) {
          const emailRequest = new FormData();
          emailRequest.append('deletedFiles', deletedImage);
          emailRequest.append('to', emailFormData.to);
          emailRequest.append('cc', emailFormData.cc);
          emailRequest.append('bcc', emailFormData.bcc);
          emailRequest.append('type', 'CONFIRM_BOOKING');
          attachment.forEach((file: any) => {
            emailRequest.append('files', file);
          });
          appendEmailAttachmentOptions(emailRequest, emailAttachmentVisibility);

          const requestHeaders = {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          };

          const { status } = await axiosInstance.post(
            URLS.sendBookingEmailAttachment(id),
            emailRequest,
            requestHeaders,
          );

          if (status === 200) {
            setMessage({
              display: true,
              severity: 'success',
              message: strings.bookingConfirmSuccess,
            });
            setTimeout(() => onSuccess(), 1000);
            return true;
          }
        }
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: `${strings.required_error_prefix_message} ${fetchErrorFields(
            formData,
            NewBookingSendEmailFormComponent(strings),
          ).join(', ')}`,
        });
        setSendEmailHasError(true);
      }
    } catch (error: any) {
      handleClose();
      if (error.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description
            ? error.response.data.error_description
            : error.response.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    } finally {
      savingRef.current = false;
      setIsLoading(false);
    }
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };
  const handleSave = async () => {
    const arr = bookingEmail === '' ? [loggedInUserDetails.email] : [loggedInUserDetails.email, bookingEmail];
    setSendEmailPageResponse({ to: arr.join(',') });
    handleClose();
    setOpenSendEmailDrawer(true);
  };

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={5000}
        />
      )}
      <Button
        fullWidth={!compact}
        size={compact ? 'small' : 'medium'}
        onClick={() => setOpenModal(true)}
        variant="contained"
        color="success"
        sx={compact ? { ...bookingDetailToolbarButtonSx, my: 0 } : { minHeight: '36px', my: 1 }}
        startIcon={<CheckCircle />}>
        {strings.bookingDetailsConfirmBooking}
      </Button>
      <NewBookingSendEmailComponent
        submitButtonText={strings.common_button_save}
        openDrawer={openSendEmailDrawer}
        formRef={sendEmailFormRef}
        handleCloseDrawer={() => setOpenSendEmailDrawer(false)}
        hasError={sendEmailHasError}
        pageResponse={sendEmailPageResponse}
        handleSave={(e: any) => handleSaveWithSendEmail(e)}
        attachment={attachment}
        deletedImage={deletedImage}
        getFileDetails={getFileDetails}
        disabled={isLoading}
        {...emailAttachmentVisibility}
      />
      <Modal
        open={openModal}
        setModalClose={handleClose}
        title=""
        size=""
        className=""
        closeButton={false}
        tone="success"
        icon={<CheckCircle />}
        contentTitle={strings.bookingDetailsConfirmBooking}
        contentText={strings.bookingDetailsModalBodyMessageConfirmBooking}
        backButtonText={strings.common_button_back}
        backButtonFunc={handleClose}
        saveButtonText={strings.bookingDetailsModalConfirmButton}
        saveButtonFunc={handleSave}
        loading={isLoading}
      />
    </>
  );
};
